import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * The languages the product actually ships.
 *
 * `de` and `fr` were listed here and imported into `resources` while carrying
 * 2,048 of ~6,637 keys — 31% coverage. `fallbackLng: 'en'` meant nothing
 * crashed and no raw key rendered, so the gap was invisible in testing; what a
 * German user actually got was a UI two-thirds in English, reached without
 * asking for it because `LanguageDetector` reads `navigator` and a `de-DE`
 * browser matched. `toggleLanguage` also cycled through all four, so pressing
 * the language button twice from Arabic stranded you in German with three more
 * presses to get home.
 *
 * The locale files are kept in the repo — they are a real head start, not
 * something to throw away — but they are no longer imported, so they cost
 * nothing in the bundle and cannot be reached until they are finished. To
 * restore one: complete it (`pnpm i18n:check --locale de` must pass), then add
 * it back here, to `Language` in LanguageContext, and to the switcher.
 */
export const SUPPORTED_LANGUAGES = ['en', 'ar'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Each language is its own chunk, loaded when it is needed.
 *
 * Both files used to be imported here, so they sat inside the entry chunk:
 * 1.6 MB of the 2 MB every visitor downloaded first, Arabic for English users
 * included, and all of it again after every deploy because the entry's hash
 * changes with any code. Split out, each file is fetched only for its
 * language and stays cached until its own text changes.
 */
const LOADERS: Record<
  SupportedLanguage,
  () => Promise<{ default: Record<string, unknown> }>
> = {
  en: () => import('./locales/en.json'),
  ar: () => import('./locales/ar.json'),
};

/** The supported language `lng` (e.g. `ar-EG`) means, or English. */
export function supportedLanguage(lng: string | undefined): SupportedLanguage {
  const base = (lng ?? '').split('-')[0] as SupportedLanguage;
  return SUPPORTED_LANGUAGES.includes(base) ? base : 'en';
}

/** Fetches a language's strings once; later calls resolve immediately. */
export async function loadLanguage(lng: SupportedLanguage): Promise<void> {
  if (i18n.hasResourceBundle(lng, 'translation')) return;
  const { default: strings } = await LOADERS[lng]();
  i18n.addResourceBundle(lng, 'translation', strings, true, true);
}

/**
 * Switches language once its strings are here, so the screen never shows
 * raw keys in between. Use this, not `i18n.changeLanguage` directly.
 */
export async function switchLanguage(lng: SupportedLanguage): Promise<void> {
  await loadLanguage(lng);
  await i18n.changeLanguage(lng);
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Filled by loadLanguage; nothing is bundled.
    resources: {},
    partialBundledLanguages: true,
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

/**
 * Settles once the detected language — and English, the fallback for any
 * key it lacks — can be shown. main.tsx renders after it; until then the
 * loader in index.html stays up.
 */
export const i18nReady: Promise<void> = (async () => {
  const lng = supportedLanguage(i18n.language);
  await Promise.all([loadLanguage(lng), loadLanguage('en')]);
  await i18n.changeLanguage(lng);
})();

export default i18n;
